import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import Home from './pages/Home';
import LoginSignup from './components/LoginSignup';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';

// Partner Components
import PartnerOverview from './partner/PartnerOverview';
import PartnerManagers from './partner/PartnerManagers';
import PartnerQueries from './partner/PartnerQueries';
import PartnerReports from './partner/PartnerReports';
import PartnerNotifications from './partner/PartnerNotifications';
import PartnerProfile from './partner/PartnerProfile';

// Manager Components
import ManagerOverview from './manager/ManagerOverview';
import ManagerQueries from './manager/ManagerQueries';
import ManagerReports from './manager/ManagerReports';
import ManagerNotifications from './manager/ManagerNotifications';
import ManagerProfile from './manager/ManagerProfile';

// Privacy Policy Pages
import PartnerPrivacyPolicy from './pages/PartnerPrivacyPolicy';
import EmployeePrivacyPolicy from './pages/EmployeePrivacyPolicy';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { currentUser, userRole, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-golden"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (userRole === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-golden"></div>
      </div>
    );
  }

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    console.warn(`[RBAC] Access Denied: User role '${userRole}' is not permitted. Redirecting...`);

    switch (userRole) {
      case 'partner':
        return <Navigate to="/partner/overview" replace />;
      case 'manager':
        return <Navigate to="/manager/overview" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <SidebarProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginSignup />} />

            {/* Partner Routes */}
            <Route path="partner" element={
              <ProtectedRoute allowedRoles={['partner']}>
                <Outlet />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<PartnerOverview />} />
              <Route path="managers" element={<PartnerManagers />} />
              <Route path="queries" element={<PartnerQueries />} />
              <Route path="reports" element={<PartnerReports />} />
              <Route path="notifications" element={<PartnerNotifications />} />
              <Route path="profile" element={<PartnerProfile />} />
            </Route>

            {/* Manager Routes */}
            <Route path="manager" element={
              <ProtectedRoute allowedRoles={['manager']}>
                <Outlet />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<ManagerOverview />} />
              <Route path="queries" element={<ManagerQueries />} />
              <Route path="reports" element={<ManagerReports />} />
              <Route path="notifications" element={<ManagerNotifications />} />
              <Route path="profile" element={<ManagerProfile />} />
            </Route>

            {/* Privacy Policy Routes */}
            <Route path="/privacy-policy/partner" element={<PartnerPrivacyPolicy />} />
            <Route path="/privacy-policy/employee" element={<EmployeePrivacyPolicy />} />

            {/* Fallback Route */}
            <Route path="*" element={
              <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h1>
                  <a href="/" className="text-brand-golden hover:underline">Go Home</a>
                </div>
              </div>
            } />
          </Routes>
        </Router>
      </SidebarProvider>
    </AuthProvider>
  );
}

export default App;
